import React from 'react';
import { Text } from '@/components/ui/text/Text';
import { Selector } from '@/components/ui/inputs/selector';
import { Skeleton } from '@/components/ui/loading';
import { useTranslation } from '@/i18n';
import type { MonthlyCutCompanyScope } from '@/api/domains/attendance';
import { DonutSummaryCard } from './DonutSummaryCard';
import { DonutSummaryCardSkeleton } from './DonutSummaryCardSkeleton';
import { getOvertimeColor, getTardinessColor, getAbsenceColor } from './attendanceThresholds';
import { colors } from '@/assets/styles/colors';

// Tailwind requiere valores arbitrarios estáticos en el código fuente, por lo que no puede
// interpolar colors.primary.general (#B2E5FF) / colors.primary.main (#1E5591) en runtime.
const CUT_FILTER_SELECTOR_CLASS_NAME =
  '!h-6 !rounded-full !border-0 !bg-[#B2E5FF] !text-[#1E5591] !pl-2 !pr-8 !py-0 flex items-center gap-2';

export interface MonthSummaryData {
  overtime: {
    percentage: number;
    approved: string;
    pending: string;
    total: string;
  };
  tardiness: {
    percentage: number;
    approved: string;
    pending: string;
    total: string;
  };
  absences: {
    percentage: number;
    unjustified: string;
    justified: string;
    total: string;
  };
  marks: {
    percentageInPerson: number;
    percentageRemote: number;
  };
}

interface MonthSummarySectionProps {
  data?: MonthSummaryData | null;
  isLoadingData?: boolean;
  selectedYear: string;
  selectedMonth: string;
  selectedCutId: string;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  cutOptions: { value: string; label: string }[];
  isCutSelectorDisabled: boolean;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
  onCutChange: (cutId: string) => void;
  isLoadingFilters?: boolean;
  companyScope: MonthlyCutCompanyScope;
  onCompanyScopeChange: (scope: MonthlyCutCompanyScope) => void;
  companyScopeOptions: { value: MonthlyCutCompanyScope; label: string }[];
  'data-testid'?: string;
}

export const MonthSummarySection: React.FC<MonthSummarySectionProps> = ({
  data,
  isLoadingData = false,
  selectedYear,
  selectedMonth,
  selectedCutId,
  yearOptions,
  monthOptions,
  cutOptions,
  isCutSelectorDisabled,
  onYearChange,
  onMonthChange,
  onCutChange,
  isLoadingFilters = false,
  companyScope,
  onCompanyScopeChange,
  companyScopeOptions,
  'data-testid': dataTestId,
}) => {
  const { t: translate } = useTranslation();
  const hoursUnit = translate('attendance:dashboard.monthSummary.units.hours');
  const daysUnit = translate('attendance:dashboard.monthSummary.units.days');

  const handleCompanyScopeChange = (value: string | string[]) => {
    const scope = Array.isArray(value) ? (value[0] ?? '') : value;
    if (scope === 'CONTRACTING' || scope === 'PARENT') onCompanyScopeChange(scope);
  };

  const handleYearChange = (value: string | string[]) => {
    onYearChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleMonthChange = (value: string | string[]) => {
    onMonthChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleCutChange = (value: string | string[]) => {
    onCutChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }} data-testid={dataTestId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Text
          variant="body-medium"
          fontFamily="montserrat"
          color={colors.grays.neutral66}
          style={{ fontWeight: 600 }}
          data-testid={`${dataTestId}-title`}
        >
          {translate('attendance:dashboard.monthSummary.title')}
        </Text>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isLoadingFilters ? (
            <>
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-company-scope-skeleton`} />
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-year-skeleton`} />
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-month-skeleton`} />
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-cut-skeleton`} />
            </>
          ) : (
            <>
              {companyScopeOptions.length > 0 && (
                <Selector
                  label={translate('attendance:dashboard.monthSummary.filters.companyScope')}
                  placeholder={translate('attendance:dashboard.monthSummary.filters.companyScope')}
                  options={companyScopeOptions}
                  value={companyScope}
                  onChange={handleCompanyScopeChange}
                  disabled={companyScopeOptions.length <= 1}
                  hideLabel
                  chevronColor={colors.primary.main}
                  chevronWrapperClassName="items-center"
                  className="w-[196px]"
                  selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                  data-testid={`${dataTestId}-company-scope-selector`}
                />
              )}
              <Selector
                label={translate('attendance:dashboard.monthSummary.filters.year')}
                placeholder={translate('attendance:dashboard.monthSummary.filters.year')}
                options={yearOptions}
                value={selectedYear || undefined}
                onChange={handleYearChange}
                hideLabel
                chevronColor={colors.primary.main}
                chevronWrapperClassName="items-center"
                className="w-[196px]"
                selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                data-testid={`${dataTestId}-year-selector`}
              />
              <Selector
                label={translate('attendance:dashboard.monthSummary.filters.month')}
                placeholder={translate('attendance:dashboard.monthSummary.filters.month')}
                options={monthOptions}
                value={selectedMonth || undefined}
                onChange={handleMonthChange}
                hideLabel
                chevronColor={colors.primary.main}
                chevronWrapperClassName="items-center"
                className="w-[196px]"
                selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                data-testid={`${dataTestId}-month-selector`}
              />
              <Selector
                label={translate('attendance:dashboard.monthSummary.filters.cut')}
                placeholder={translate('attendance:dashboard.monthSummary.filters.cut')}
                options={cutOptions}
                value={selectedCutId || undefined}
                onChange={handleCutChange}
                disabled={isCutSelectorDisabled}
                hideLabel
                chevronColor={colors.primary.main}
                chevronWrapperClassName="items-center"
                className="w-[196px]"
                selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                data-testid={`${dataTestId}-cut-selector`}
              />
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {(isLoadingData || !data) ? (
          <>
            <DonutSummaryCardSkeleton data-testid={`${dataTestId}-horas-extra-skeleton`} />
            <DonutSummaryCardSkeleton data-testid={`${dataTestId}-atrasos-skeleton`} />
            <DonutSummaryCardSkeleton data-testid={`${dataTestId}-ausencias-skeleton`} />
            <DonutSummaryCardSkeleton rows={2} data-testid={`${dataTestId}-marcajes-skeleton`} />
          </>
        ) : (
          <>
            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.horasExtra.title')}
              percentage={data.overtime.percentage}
              color={getOvertimeColor(data.overtime.percentage)}
              percentageLabel={`${data.overtime.percentage}%`}
              rows={[
                { label: translate('attendance:dashboard.monthSummary.horasExtra.approved'), value: `${data.overtime.approved} ${hoursUnit}`, valueColor: getOvertimeColor(data.overtime.percentage) },
                { label: translate('attendance:dashboard.monthSummary.horasExtra.pending'), value: `${data.overtime.pending} ${hoursUnit}` },
                { label: translate('attendance:dashboard.monthSummary.horasExtra.total'), value: `${data.overtime.total} ${hoursUnit}` },
              ]}
              data-testid={`${dataTestId}-horas-extra`}
            />

            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.atrasos.title')}
              percentage={data.tardiness.percentage}
              color={getTardinessColor(data.tardiness.percentage)}
              percentageLabel={`${data.tardiness.percentage}%`}
              rows={[
                { label: translate('attendance:dashboard.monthSummary.atrasos.approved'), value: `${data.tardiness.approved} ${hoursUnit}`, valueColor: getTardinessColor(data.tardiness.percentage) },
                { label: translate('attendance:dashboard.monthSummary.atrasos.pending'), value: `${data.tardiness.pending} ${hoursUnit}` },
                { label: translate('attendance:dashboard.monthSummary.atrasos.total'), value: `${data.tardiness.total} ${hoursUnit}` },
              ]}
              data-testid={`${dataTestId}-atrasos`}
            />

            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.ausencias.title')}
              percentage={data.absences.percentage}
              color={getAbsenceColor(data.absences.percentage)}
              percentageLabel={`${data.absences.percentage}%`}
              rows={[
                { label: translate('attendance:dashboard.monthSummary.ausencias.unjustified'), value: `${data.absences.unjustified} ${daysUnit}`, valueColor: getAbsenceColor(data.absences.percentage) },
                { label: translate('attendance:dashboard.monthSummary.ausencias.justified'), value: `${data.absences.justified} ${daysUnit}` },
                { label: translate('attendance:dashboard.monthSummary.ausencias.total'), value: `${data.absences.total} ${daysUnit}` },
              ]}
              data-testid={`${dataTestId}-ausencias`}
            />

            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.marcajes.title')}
              segments={[
                { percentage: data.marks.percentageInPerson, color: colors.primary.main },
                { percentage: data.marks.percentageRemote, color: colors.dashboardStatus.remoto },
              ]}
              labelColor={colors.primary.main}
              percentageLabel={`${data.marks.percentageInPerson}%`}
              percentageSubLabel={translate('attendance:dashboard.monthSummary.marcajes.presencial')}
              rows={[
                {
                  label: translate('attendance:dashboard.monthSummary.marcajes.presencial'),
                  value: `${data.marks.percentageInPerson} %`,
                  valueColor: colors.primary.main,
                },
                {
                  label: translate('attendance:dashboard.monthSummary.marcajes.remoto'),
                  value: `${data.marks.percentageRemote} %`,
                  valueColor: colors.dashboardStatus.remoto,
                },
              ]}
              data-testid={`${dataTestId}-marcajes`}
            />
          </>
        )}
      </div>
    </div>
  );
};
